import React, { useEffect, useState, useCallback } from 'react';
import {
    BarChart2, TrendingUp, FileText, Mail, Megaphone,
    Globe, RefreshCw, AlertCircle, CheckCircle, Clock,
    Activity, Target, Zap, Info, Newspaper, Layout,
    ExternalLink, Unplug, Users, Eye, Timer, MousePointerClick,
} from 'lucide-react';
import {
    GlassCard, GlassButton, GlassSelect, GlassTabs, GlassBadge,
    KPICard, ChartCard, ModuleHeader,
} from '../ui';
import RevenueAnalytics from './RevenueAnalytics';
import UtmBreakdownPanel from './UtmBreakdownPanel';
import {
    getAnalyticsOverview, getCampaignAnalytics, getEmailMetrics,
    getContentPerformance, generateMonthlySummary, getTrafficPlaceholder,
    getCampaigns, getProject, getTraffic, startGAConnect, listGAProperties,
    selectGAProperty, disconnectGA,
    type TrafficResponse, type GAProperty,
} from '../../services/api';
import { useOutletContext, useLocation, useNavigate } from 'react-router-dom';
import './Analytics.css';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface OutletCtx {
    selectedProjectId?: string;
}

const TABS = [
    { id: 'overview', label: 'Overview' },
    { id: 'campaign', label: 'Campaign Performance' },
    { id: 'email', label: 'Email Metrics' },
    { id: 'content', label: 'Content Performance' },
    { id: 'revenue', label: 'Revenue' },
    { id: 'monthly', label: 'Monthly Summary' },
    { id: 'traffic', label: 'Traffic' },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(iso: string | null | undefined): string {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
        return iso;
    }
}

function capitalize(s: string): string {
    return s ? s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, ' ') : s;
}

// Friendlier labels for known channel keys — strip the "social_" prefix
// and capitalise platform names properly, plus a few quick-win-type aliases.
const CHANNEL_LABELS: Record<string, string> = {
    social_linkedin: 'LinkedIn',
    social_twitter: 'Twitter / X',
    social_instagram: 'Instagram',
    social_facebook: 'Facebook',
    blog_post: 'Blog Post',
    landing_page: 'Landing Page',
    email: 'Email',
    seo_metadata: 'SEO Metadata',
    ad_copy: 'Ad Copy',
    keyword_research: 'Keyword Research',
    content_calendar: 'Content Calendar',
    carousel: 'Carousel',
    video_script: 'Video Script',
    pull_quotes: 'Pull Quotes',
};

function channelLabel(key: string): string {
    return CHANNEL_LABELS[key] || capitalize(key);
}

function getStatusVariant(s: string): 'success' | 'warning' | 'info' | 'default' | 'danger' {
    if (s === 'published' || s === 'active' || s === 'approved' || s === 'completed') return 'success';
    if (s === 'draft' || s === 'pending') return 'default';
    if (s === 'generating' || s === 'processing') return 'warning';
    return 'default';
}

function getGoalVariant(g: string): 'success' | 'warning' | 'info' | 'default' {
    if (g === 'lead_gen') return 'info';
    if (g === 'promotion') return 'warning';
    if (g === 'retention') return 'success';
    return 'default';
}

function rateClass(rate: number): string {
    if (rate >= 25) return 'email-rate-good';
    if (rate >= 15) return 'email-rate-avg';
    return 'email-rate-poor';
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface BarChartProps {
    data: Record<string, number>;
    barColor?: string;
    maxOverride?: number;
}

const SimpleBarChart: React.FC<BarChartProps> = ({ data, maxOverride }) => {
    const entries = Object.entries(data).filter(([, v]) => v > 0);
    if (entries.length === 0) {
        return <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)', margin: 0 }}>No data yet.</p>;
    }
    const max = maxOverride ?? Math.max(...entries.map(([, v]) => v), 1);
    return (
        <div className="analytics-bar-list">
            {entries.map(([key, val]) => (
                <div key={key} className="analytics-bar-item">
                    <div className="analytics-bar-label-row">
                        <span className="analytics-bar-label">{channelLabel(key)}</span>
                        <span className="analytics-bar-count">{val}</span>
                    </div>
                    <div className="analytics-bar-track">
                        <div
                            className="analytics-bar-fill"
                            style={{ width: `${Math.round((val / max) * 100)}%` }}
                        />
                    </div>
                </div>
            ))}
        </div>
    );
};

const StatusBarChart: React.FC<{ data: Record<string, number> }> = ({ data }) => {
    const colorMap: Record<string, string> = {
        draft: 'info',
        approved: 'success',
        published: 'warning',
    };
    const entries = Object.entries(data);
    const max = Math.max(...entries.map(([, v]) => v), 1);
    return (
        <div className="analytics-bar-list">
            {entries.map(([key, val]) => (
                <div key={key} className="analytics-bar-item">
                    <div className="analytics-bar-label-row">
                        <span className="analytics-bar-label">{capitalize(key)}</span>
                        <span className="analytics-bar-count">{val}</span>
                    </div>
                    <div className="analytics-bar-track">
                        <div
                            className={`analytics-bar-fill ${colorMap[key] || ''}`}
                            style={{ width: `${Math.round((val / max) * 100)}%` }}
                        />
                    </div>
                </div>
            ))}
        </div>
    );
};

// ---------------------------------------------------------------------------
// Tab: Overview
// ---------------------------------------------------------------------------

const OverviewTab: React.FC<{ projectId?: string }> = ({ projectId }) => {
    const [data, setData] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const d = await getAnalyticsOverview(projectId);
            setData(d);
        } catch (e: any) {
            setError('Failed to load overview data.');
        } finally {
            setLoading(false);
        }
    }, [projectId]);

    useEffect(() => { load(); }, [load]);

    if (loading) return (
        <div className="analytics-loading">
            <RefreshCw size={18} style={{ animation: 'spin 1s linear infinite' }} />
            Loading overview…
        </div>
    );
    if (error) return (
        <div className="analytics-empty">
            <AlertCircle size={18} /> {error}
        </div>
    );
    if (!data) return null;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
            {/* KPI Row */}
            <div className="analytics-kpi-row">
                <KPICard
                    value={data.total_campaigns}
                    label="Total Campaigns"
                    icon={<Megaphone size={20} />}
                />
                <KPICard
                    value={data.active_campaigns}
                    label="Active Campaigns"
                    icon={<Activity size={20} />}
                />
                <KPICard
                    value={data.total_assets_generated}
                    label="Assets Generated"
                    icon={<FileText size={20} />}
                />
                <KPICard
                    value={data.total_email_sequences}
                    label="Email Sequences"
                    icon={<Mail size={20} />}
                />
            </div>

            {/* Charts row */}
            <div className="analytics-grid-2">
                <ChartCard title="Campaigns by Goal" height="auto">
                    <SimpleBarChart data={data.campaigns_by_goal || {}} />
                </ChartCard>

                <ChartCard title="Content by Status" height="auto">
                    <StatusBarChart data={data.content_by_status || {}} />
                </ChartCard>
            </div>

            {/* Recent Campaigns */}
            <GlassCard variant="default" padding="md">
                <h3 className="analytics-section-title">Recent Campaigns</h3>
                {(data.recent_campaigns || []).length === 0 ? (
                    <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)', margin: 0 }}>
                        No campaigns yet. Create one to see it here.
                    </p>
                ) : (
                    <table className="analytics-table">
                        <thead>
                            <tr>
                                <th>Campaign</th>
                                <th>Goal</th>
                                <th>Status</th>
                                <th>Assets</th>
                                <th>Created</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.recent_campaigns.map((c: any) => (
                                <tr key={c.campaign_id}>
                                    <td style={{ fontWeight: 'var(--font-medium)' }}>{c.name}</td>
                                    <td><GlassBadge size="sm" variant={getGoalVariant(c.goal)}>{capitalize(c.goal)}</GlassBadge></td>
                                    <td><GlassBadge size="sm" variant={getStatusVariant(c.status)}>{capitalize(c.status)}</GlassBadge></td>
                                    <td>{c.asset_count}</td>
                                    <td style={{ color: 'var(--text-secondary)' }}>{formatDate(c.created_at)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </GlassCard>

            {/* CRM Sync summary */}
            <div className="analytics-grid-2">
                <GlassCard variant="default" padding="md">
                    <h3 className="analytics-section-title">CRM Sync Status</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>Campaigns synced to CRM</span>
                            <span style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-semibold)', color: 'var(--text)' }}>
                                {data.campaigns_synced_to_crm} / {data.total_campaigns}
                            </span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>Sequences synced to CRM</span>
                            <span style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-semibold)', color: 'var(--text)' }}>
                                {data.sequences_synced_to_crm} / {data.total_email_sequences}
                            </span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>Total email steps</span>
                            <span style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-semibold)', color: 'var(--text)' }}>
                                {data.total_email_steps}
                            </span>
                        </div>
                    </div>
                </GlassCard>
            </div>
        </div>
    );
};

// ---------------------------------------------------------------------------
// Tab: Campaign Performance
// ---------------------------------------------------------------------------

const CampaignPerformanceTab: React.FC = () => {
    const [campaigns, setCampaigns] = useState<any[]>([]);
    const [selectedId, setSelectedId] = useState<string>('');
    const [campData, setCampData] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [loadingList, setLoadingList] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        (async () => {
            try {
                const list = await getCampaigns();
                setCampaigns(list);
            } catch {
                // non-fatal
            } finally {
                setLoadingList(false);
            }
        })();
    }, []);

    const loadCampaign = useCallback(async (id: string) => {
        if (!id) { setCampData(null); return; }
        setLoading(true);
        setError(null);
        try {
            const d = await getCampaignAnalytics(id);
            setCampData(d);
        } catch (e: any) {
            setError('Failed to load campaign analytics.');
        } finally {
            setLoading(false);
        }
    }, []);

    const handleSelect = (val: string) => {
        setSelectedId(val);
        loadCampaign(val);
    };

    const campOptions = campaigns.map(c => ({ value: c.campaign_id, label: c.name }));

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
            <GlassCard variant="default" padding="md">
                <div className="campaign-select-area">
                    <div className="campaign-select-wrapper">
                        <GlassSelect
                            label="Select Campaign"
                            options={campOptions}
                            value={selectedId}
                            onChange={handleSelect}
                            placeholder={loadingList ? 'Loading campaigns…' : 'Choose a campaign…'}
                            disabled={loadingList}
                        />
                    </div>
                </div>
            </GlassCard>

            {!selectedId && (
                <div className="analytics-empty">
                    <BarChart2 size={18} /> Select a campaign above to see its performance data.
                </div>
            )}

            {loading && (
                <div className="analytics-loading">
                    <RefreshCw size={18} style={{ animation: 'spin 1s linear infinite' }} />
                    Loading campaign data…
                </div>
            )}

            {error && (
                <div className="analytics-empty">
                    <AlertCircle size={18} /> {error}
                </div>
            )}

            {campData && !loading && (
                <>
                    {/* Campaign header */}
                    <GlassCard variant="default" padding="md">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 'var(--space-3)' }}>
                            <div>
                                <h2 style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--font-semibold)', color: 'var(--text)', margin: 0 }}>
                                    {campData.campaign.name}
                                </h2>
                                <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', margin: 'var(--space-1) 0 0 0' }}>
                                    {campData.campaign.start_date ? `${formatDate(campData.campaign.start_date)} → ${formatDate(campData.campaign.end_date)}` : 'No dates set'}
                                </p>
                            </div>
                            <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                                <GlassBadge variant={getGoalVariant(campData.campaign.goal)}>{capitalize(campData.campaign.goal)}</GlassBadge>
                                <GlassBadge variant={getStatusVariant(campData.campaign.status)}>{capitalize(campData.campaign.status)}</GlassBadge>
                            </div>
                        </div>
                    </GlassCard>

                    <div className="campaign-analytics-grid">
                        {/* Assets breakdown */}
                        <ChartCard title="Assets by Type" height="auto">
                            <SimpleBarChart data={campData.assets.by_type || {}} />
                        </ChartCard>

                        <ChartCard title="Assets by Status" height="auto">
                            <StatusBarChart data={campData.assets.by_status || {}} />
                        </ChartCard>
                    </div>

                    {/* Email sequences */}
                    <GlassCard variant="default" padding="md">
                        <h3 className="analytics-section-title">Email Sequences ({campData.email_sequences.length})</h3>
                        {campData.email_sequences.length === 0 ? (
                            <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)', margin: 0 }}>No email sequences for this campaign.</p>
                        ) : (
                            <div className="sequence-list">
                                {campData.email_sequences.map((seq: any) => (
                                    <div key={seq.sequence_id} className="sequence-item">
                                        <div>
                                            <div className="sequence-item-name">{seq.name}</div>
                                            <div className="sequence-item-meta">{seq.steps} steps · trigger: {capitalize(seq.trigger_type || 'manual')}</div>
                                        </div>
                                        <GlassBadge size="sm" variant={seq.synced_to_crm ? 'success' : 'default'}>
                                            {seq.synced_to_crm ? 'Synced to CRM' : 'Not synced'}
                                        </GlassBadge>
                                    </div>
                                ))}
                            </div>
                        )}
                    </GlassCard>

                    {/* CRM status */}
                    <GlassCard variant="default" padding="md">
                        <h3 className="analytics-section-title">CRM Sync</h3>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                            {campData.crm_metrics.synced ? (
                                <>
                                    <CheckCircle size={18} style={{ color: 'var(--success)' }} />
                                    <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text)' }}>
                                        Synced to SuiteCRM{campData.crm_metrics.last_synced ? ` · ${formatDate(campData.crm_metrics.last_synced)}` : ''}
                                    </span>
                                </>
                            ) : (
                                <>
                                    <Clock size={18} style={{ color: 'var(--text-secondary)' }} />
                                    <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>Not yet synced to CRM</span>
                                </>
                            )}
                        </div>
                    </GlassCard>
                </>
            )}
        </div>
    );
};

// ---------------------------------------------------------------------------
// Tab: Email Metrics
// ---------------------------------------------------------------------------

const EmailMetricsTab: React.FC = () => {
    const [data, setData] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        (async () => {
            try {
                const d = await getEmailMetrics();
                setData(d);
            } catch (e: any) {
                setError('Failed to load email metrics.');
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    if (loading) return (
        <div className="analytics-loading">
            <RefreshCw size={18} style={{ animation: 'spin 1s linear infinite' }} />
            Loading email metrics…
        </div>
    );

    if (error) return (
        <div className="analytics-empty"><AlertCircle size={18} /> {error}</div>
    );

    if (!data || data.campaigns.length === 0) {
        return (
            <GlassCard variant="default" padding="md">
                <div className="connect-crm-card">
                    <div className="traffic-placeholder-icon">
                        <Mail size={32} />
                    </div>
                    <h3 className="traffic-placeholder-title">No Email Metrics Available</h3>
                    <p className="traffic-placeholder-desc">
                        {data?.note || 'Connect SuiteCRM and sync campaigns to see email open rates, click rates, and engagement metrics.'}
                    </p>
                    <GlassButton variant="secondary" icon={<TrendingUp size={16} />} onClick={() => window.location.href = '/leads'}>
                        Go to CRM
                    </GlassButton>
                </div>
            </GlassCard>
        );
    }

    return (
        <GlassCard variant="default" padding="md">
            <h3 className="analytics-section-title">Email Campaign Performance</h3>
            <table className="email-metrics-table">
                <thead>
                    <tr>
                        <th>Campaign</th>
                        <th>Sent</th>
                        <th>Opens</th>
                        <th>Open Rate</th>
                        <th>Clicks</th>
                        <th>Click Rate</th>
                        <th>Unsubscribes</th>
                    </tr>
                </thead>
                <tbody>
                    {data.campaigns.map((c: any) => (
                        <tr key={c.crm_campaign_id}>
                            <td style={{ fontWeight: 'var(--font-medium)' }}>{c.name}</td>
                            <td>{c.emails_sent.toLocaleString()}</td>
                            <td>{c.opens.toLocaleString()}</td>
                            <td className={rateClass(c.open_rate)}>{c.open_rate.toFixed(1)}%</td>
                            <td>{c.clicks.toLocaleString()}</td>
                            <td className={rateClass(c.click_rate)}>{c.click_rate.toFixed(1)}%</td>
                            <td>{c.unsubscribes}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </GlassCard>
    );
};

// ---------------------------------------------------------------------------
// Tab: Content Performance
// ---------------------------------------------------------------------------

const ContentPerformanceTab: React.FC<{ projectId?: string }> = ({ projectId }) => {
    const [data, setData] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        (async () => {
            try {
                const d = await getContentPerformance(projectId);
                setData(d);
            } catch {
                setError('Failed to load content performance.');
            } finally {
                setLoading(false);
            }
        })();
    }, [projectId]);

    if (loading) return (
        <div className="analytics-loading">
            <RefreshCw size={18} style={{ animation: 'spin 1s linear infinite' }} />
            Loading content data…
        </div>
    );
    if (error) return <div className="analytics-empty"><AlertCircle size={18} /> {error}</div>;
    if (!data) return null;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
            <div className="analytics-kpi-row">
                <KPICard value={data.total_pieces_generated} label="Total Pieces Generated" icon={<FileText size={20} />} />
                <KPICard value={data.by_status?.published || 0} label="Articles Published" icon={<Newspaper size={20} />} />
                <KPICard value={data.landing_pages_published || 0} label="Landing Pages Published" icon={<Layout size={20} />} />
                <KPICard value={data.by_status?.approved || 0} label="Approved" icon={<CheckCircle size={20} />} />
                <KPICard value={data.by_status?.draft || 0} label="In Draft" icon={<Clock size={20} />} />
            </div>

            <div className="analytics-grid-2">
                <ChartCard title="Content by Channel" height="auto">
                    <SimpleBarChart data={data.by_channel || {}} />
                </ChartCard>

                <ChartCard title="Content by Status" height="auto">
                    <StatusBarChart data={data.by_status || {}} />
                </ChartCard>
            </div>

            <GlassCard variant="default" padding="md">
                <h3 className="analytics-section-title">Recent Activity</h3>
                {(data.recent_activity || []).length === 0 ? (
                    <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)', margin: 0 }}>
                        No content activity yet.
                    </p>
                ) : (
                    <div className="activity-list">
                        {data.recent_activity.map((item: any, i: number) => (
                            <div key={i} className="activity-item">
                                <div className="activity-icon">
                                    <FileText size={16} />
                                </div>
                                <span className="activity-title">{item.title}</span>
                                <span className="activity-meta" style={{ marginRight: 'var(--space-2)' }}>{capitalize(item.type)}</span>
                                <GlassBadge size="sm" variant={getStatusVariant(item.status)}>{capitalize(item.status)}</GlassBadge>
                                <span className="activity-meta">{formatDate(item.created_at)}</span>
                            </div>
                        ))}
                    </div>
                )}
            </GlassCard>
        </div>
    );
};

// ---------------------------------------------------------------------------
// Tab: Monthly Summary
// ---------------------------------------------------------------------------

const MonthlySummaryTab: React.FC<{ projectId?: string }> = ({ projectId }) => {
    const now = new Date();
    const defaultMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

    const [month, setMonth] = useState(defaultMonth);
    const [summary, setSummary] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleGenerate = async () => {
        if (!projectId) {
            setError('Please select a project first.');
            return;
        }
        setLoading(true);
        setError(null);
        setSummary(null);
        try {
            const d = await generateMonthlySummary({ project_id: projectId, month });
            setSummary(d);
        } catch (e: any) {
            setError('Failed to generate summary. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
            <GlassCard variant="default" padding="md">
                <div className="summary-controls">
                    <div className="summary-month-input">
                        <label style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)', color: 'var(--text-secondary)', display: 'block', marginBottom: 'var(--space-1)' }}>
                            Select Month
                        </label>
                        <input
                            type="month"
                            value={month}
                            onChange={e => setMonth(e.target.value)}
                            style={{
                                width: '100%',
                                padding: 'var(--space-3) var(--space-4)',
                                fontSize: 'var(--text-base)',
                                fontFamily: 'var(--font-family)',
                                color: 'var(--text)',
                                background: 'var(--panel)',
                                border: '1px solid var(--stroke)',
                                borderRadius: 'var(--radius-md)',
                                backdropFilter: 'blur(20px)',
                                outline: 'none',
                            }}
                        />
                    </div>
                    <GlassButton
                        variant="primary"
                        icon={<Zap size={16} />}
                        onClick={handleGenerate}
                        loading={loading}
                        disabled={!projectId}
                        title={!projectId ? 'Select a project first' : undefined}
                    >
                        Generate Summary
                    </GlassButton>
                </div>
                {!projectId && (
                    <p style={{ fontSize: 'var(--text-sm)', color: 'var(--warning)', marginTop: 'var(--space-3)', marginBottom: 0 }}>
                        Select a project from the sidebar to generate a summary.
                    </p>
                )}
            </GlassCard>

            {error && (
                <GlassCard variant="default" padding="md">
                    <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center', color: 'var(--danger)' }}>
                        <AlertCircle size={18} />
                        <span style={{ fontSize: 'var(--text-sm)' }}>{error}</span>
                    </div>
                </GlassCard>
            )}

            {loading && (
                <GlassCard variant="default" padding="md">
                    <div className="analytics-loading">
                        <RefreshCw size={18} style={{ animation: 'spin 1s linear infinite' }} />
                        Gemini is generating your monthly summary…
                    </div>
                </GlassCard>
            )}

            {summary && !loading && (
                <div className="summary-result">
                    {/* Headline */}
                    <GlassCard variant="default" padding="md">
                        <p style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', marginBottom: 'var(--space-2)', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 var(--space-2) 0' }}>
                            {summary.month_label}
                        </p>
                        <h2 className="summary-headline">{summary.summary?.headline || 'No headline generated.'}</h2>
                    </GlassCard>

                    {/* Data snapshot */}
                    <GlassCard variant="default" padding="md">
                        <h3 className="analytics-section-title">Data Used</h3>
                        <div className="data-snapshot-row">
                            <div className="data-snapshot-item">
                                <span className="data-snapshot-value">{summary.data_snapshot?.campaigns}</span>
                                <span className="data-snapshot-label">Campaigns</span>
                            </div>
                            <div className="data-snapshot-item">
                                <span className="data-snapshot-value">{summary.data_snapshot?.assets}</span>
                                <span className="data-snapshot-label">Assets</span>
                            </div>
                            <div className="data-snapshot-item">
                                <span className="data-snapshot-value">{summary.data_snapshot?.sequences}</span>
                                <span className="data-snapshot-label">Sequences</span>
                            </div>
                            <div className="data-snapshot-item">
                                <span className="data-snapshot-value">{summary.data_snapshot?.email_steps}</span>
                                <span className="data-snapshot-label">Email Steps</span>
                            </div>
                        </div>
                    </GlassCard>

                    <div className="analytics-grid-2">
                        {/* Highlights */}
                        <GlassCard variant="default" padding="md">
                            <h3 className="analytics-section-title">Highlights</h3>
                            <ul className="summary-highlights">
                                {(summary.summary?.highlights || []).map((h: string, i: number) => (
                                    <li key={i}>{h}</li>
                                ))}
                            </ul>
                        </GlassCard>

                        {/* Content insight */}
                        <GlassCard variant="default" padding="md">
                            <h3 className="analytics-section-title">Content Insight</h3>
                            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text)', lineHeight: 1.6, margin: 0 }}>
                                {summary.summary?.top_content_insight || '—'}
                            </p>
                        </GlassCard>
                    </div>

                    {/* Recommendations */}
                    <GlassCard variant="default" padding="md">
                        <h3 className="analytics-section-title">Recommendations for Next Month</h3>
                        <ul className="summary-recommendations">
                            {(summary.summary?.recommendations || []).map((r: string, i: number) => (
                                <li key={i}>
                                    <Target size={14} style={{ color: 'var(--primary-500)', flexShrink: 0, marginTop: 2 }} />
                                    {r}
                                </li>
                            ))}
                        </ul>
                    </GlassCard>

                    {/* Focus areas */}
                    <GlassCard variant="default" padding="md">
                        <h3 className="analytics-section-title">Priority Focus Areas</h3>
                        <div className="summary-focus-tags">
                            {(summary.summary?.focus_areas || []).map((f: string, i: number) => (
                                <span key={i} className="summary-focus-tag">{f}</span>
                            ))}
                        </div>
                    </GlassCard>

                    {/* GA info card */}
                    <GlassCard variant="default" padding="md">
                        <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'flex-start' }}>
                            <Info size={18} style={{ color: 'var(--info)', flexShrink: 0, marginTop: 2 }} />
                            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.6 }}>
                                Traffic data (sessions, pageviews, conversions) requires a Google Analytics connection.
                                See the <strong>Traffic</strong> tab for setup instructions.
                            </p>
                        </div>
                    </GlassCard>
                </div>
            )}
        </div>
    );
};

// ---------------------------------------------------------------------------
// Traffic helpers
// ---------------------------------------------------------------------------

function formatSeconds(secs: number): string {
    const m = Math.floor(secs / 60);
    const s = Math.round(secs % 60);
    if (m === 0) return `${s}s`;
    return `${m}m ${s}s`;
}

// Minimal SVG line chart (no recharts dependency needed)
interface MiniLineChartProps {
    data: { date: string; page_views: number; unique_visitors: number }[];
}

const MiniLineChart: React.FC<MiniLineChartProps> = ({ data }) => {
    if (!data || data.length === 0) return null;
    const W = 600;
    const H = 160;
    const PAD = { top: 12, right: 16, bottom: 32, left: 48 };
    const innerW = W - PAD.left - PAD.right;
    const innerH = H - PAD.top - PAD.bottom;

    const maxVal = Math.max(...data.map(d => Math.max(d.page_views, d.unique_visitors)), 1);
    const xStep = data.length > 1 ? innerW / (data.length - 1) : innerW;

    const toX = (i: number) => PAD.left + i * xStep;
    const toY = (v: number) => PAD.top + innerH - (v / maxVal) * innerH;

    const pathFor = (key: 'page_views' | 'unique_visitors') =>
        data.map((d, i) => `${i === 0 ? 'M' : 'L'}${toX(i).toFixed(1)},${toY(d[key]).toFixed(1)}`).join(' ');

    // Y axis ticks
    const yTicks = [0, 0.25, 0.5, 0.75, 1].map(f => ({
        y: PAD.top + innerH - f * innerH,
        label: Math.round(f * maxVal).toLocaleString(),
    }));

    // X axis labels — show up to 7 evenly spaced
    const xLabelIdx: number[] = [];
    if (data.length <= 7) {
        for (let i = 0; i < data.length; i++) xLabelIdx.push(i);
    } else {
        const step = Math.ceil((data.length - 1) / 6);
        for (let i = 0; i < data.length; i += step) xLabelIdx.push(i);
        if (xLabelIdx[xLabelIdx.length - 1] !== data.length - 1) xLabelIdx.push(data.length - 1);
    }

    return (
        <div style={{ overflowX: 'auto' }}>
            <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', minWidth: 300, display: 'block' }}>
                {/* Grid lines */}
                {yTicks.map((t, i) => (
                    <g key={i}>
                        <line x1={PAD.left} y1={t.y} x2={W - PAD.right} y2={t.y} stroke="var(--stroke)" strokeWidth="1" />
                        <text x={PAD.left - 6} y={t.y + 4} textAnchor="end" fontSize="10" fill="var(--text-secondary)">{t.label}</text>
                    </g>
                ))}

                {/* Lines */}
                <path d={pathFor('page_views')} fill="none" stroke="var(--primary-500)" strokeWidth="2" strokeLinejoin="round" />
                <path d={pathFor('unique_visitors')} fill="none" stroke="#1EACA6" strokeWidth="2" strokeLinejoin="round" />

                {/* X labels */}
                {xLabelIdx.map(i => (
                    <text key={i} x={toX(i)} y={H - 4} textAnchor="middle" fontSize="10" fill="var(--text-secondary)">
                        {data[i].date.slice(5)}
                    </text>
                ))}
            </svg>

            {/* Legend */}
            <div style={{ display: 'flex', gap: 'var(--space-4)', marginTop: 'var(--space-2)', paddingLeft: PAD.left }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', fontSize: 'var(--text-xs)', color: 'var(--text-secondary)' }}>
                    <span style={{ display: 'inline-block', width: 20, height: 2, background: 'var(--primary-500)', borderRadius: 1 }} />
                    Page Views
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', fontSize: 'var(--text-xs)', color: 'var(--text-secondary)' }}>
                    <span style={{ display: 'inline-block', width: 20, height: 2, background: '#1EACA6', borderRadius: 1 }} />
                    Unique Visitors
                </div>
            </div>
        </div>
    );
};

// ---------------------------------------------------------------------------
// GA Connect Card
// ---------------------------------------------------------------------------

interface GAConnectCardProps {
    projectId: string;
    gaConnectedEmail: string | null | undefined;
    gaPropertyId: string | null | undefined;
    gaPropertyName?: string | null;
    onConnected: () => void;
}

// Feature flag: hide the live OAuth flow until Google's app verification clears
// (analytics.readonly is a sensitive scope; production needs Google review).
// Default: disabled — renders a "Coming soon · in review with Google" card.
// To enable: set REACT_APP_GA_INTEGRATION_ENABLED=true in frontend/.env and rebuild.
// Backdoor for internal dogfood (no rebuild needed): localStorage.setItem('aime_ga_test', '1')
const GA_INTEGRATION_ENABLED =
    process.env.REACT_APP_GA_INTEGRATION_ENABLED === 'true' ||
    (typeof window !== 'undefined' && window.localStorage?.getItem('aime_ga_test') === '1');

// Coming-soon variant — rendered when GA_INTEGRATION_ENABLED is false. Pure
// presentation, no hooks, so the parent can early-return without violating
// React's rules-of-hooks.
const GAComingSoonCard: React.FC = () => (
    <GlassCard variant="default" padding="md" style={{ opacity: 0.7 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
                <Globe size={20} style={{ color: 'var(--text-secondary)', flexShrink: 0 }} />
                <h3 style={{ margin: 0, fontSize: 'var(--text-base)', fontWeight: 'var(--font-semibold)', color: 'var(--text-secondary)' }}>
                    Google Analytics
                </h3>
                <GlassBadge size="sm" variant="warning">Coming soon · in review with Google</GlassBadge>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)', flexWrap: 'wrap' }}>
                <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', flex: 1 }}>
                    Connect your GA4 property to overlay externally-hosted site traffic in this tab. We're currently completing Google's app verification — this card will activate automatically once approved.
                </p>
                <GlassButton
                    variant="primary"
                    icon={<ExternalLink size={15} />}
                    disabled
                    title="Awaiting Google OAuth verification — typically 2-4 weeks. The button will activate once approved."
                >
                    Connect Google Analytics
                </GlassButton>
            </div>
        </div>
    </GlassCard>
);

const GAConnectCard: React.FC<GAConnectCardProps> = ({ projectId, gaConnectedEmail, gaPropertyId, gaPropertyName, onConnected }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const params = new URLSearchParams(location.search);
    const gaConnectedParam = params.get('ga_connected');
    const gaErrorParam = params.get('ga_error');

    const [properties, setProperties] = useState<GAProperty[]>([]);
    const [loadingProps, setLoadingProps] = useState(false);
    const [selectedProp, setSelectedProp] = useState('');
    const [connecting, setConnecting] = useState(false);
    const [disconnecting, setDisconnecting] = useState(false);
    const [savingProp, setSavingProp] = useState(false);
    const [localError, setLocalError] = useState<string | null>(null);

    // Load properties after OAuth callback
    useEffect(() => {
        if (gaConnectedParam === '1' && projectId) {
            setLoadingProps(true);
            listGAProperties(projectId)
                .then(res => setProperties(res.properties))
                .catch(() => setLocalError('Failed to load GA properties. Please try again.'))
                .finally(() => setLoadingProps(false));
        }
    }, [gaConnectedParam, projectId]);

    const handleConnect = async () => {
        if (!projectId) return;
        setConnecting(true);
        setLocalError(null);
        try {
            const res = await startGAConnect(projectId);
            window.location.href = res.auth_url;
        } catch {
            setLocalError('Failed to start Google Analytics connection. Please try again.');
            setConnecting(false);
        }
    };

    const handleSelectProperty = async () => {
        if (!selectedProp || !projectId) return;
        const prop = properties.find(p => p.property_id === selectedProp);
        if (!prop) return;
        setSavingProp(true);
        setLocalError(null);
        try {
            await selectGAProperty(projectId, prop.property_id, prop.display_name);
            // Remove the ga_connected param from the URL
            navigate(location.pathname, { replace: true });
            onConnected();
        } catch {
            setLocalError('Failed to save property selection. Please try again.');
        } finally {
            setSavingProp(false);
        }
    };

    const handleDisconnect = async () => {
        if (!projectId) return;
        setDisconnecting(true);
        setLocalError(null);
        try {
            await disconnectGA(projectId);
            onConnected();
        } catch {
            setLocalError('Failed to disconnect Google Analytics. Please try again.');
        } finally {
            setDisconnecting(false);
        }
    };

    // The token pair is what makes it connected; the email is display-only (the OAuth
    // scope doesn't include email, so /userinfo can return nothing and the column stays '').
    const isConnectedWithProperty = !!gaPropertyId;

    return (
        <GlassCard variant="default" padding="md">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                    <Globe size={20} style={{ color: 'var(--primary-500)', flexShrink: 0 }} />
                    <h3 style={{ margin: 0, fontSize: 'var(--text-base)', fontWeight: 'var(--font-semibold)', color: 'var(--text)' }}>
                        Google Analytics
                    </h3>
                    {isConnectedWithProperty && (
                        <GlassBadge size="sm" variant="success">Connected</GlassBadge>
                    )}
                </div>

                {/* Error from OAuth callback */}
                {gaErrorParam && (
                    <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'flex-start', padding: 'var(--space-3)', background: 'rgba(239,68,68,0.08)', borderRadius: 'var(--radius-md)', border: '1px solid rgba(239,68,68,0.2)' }}>
                        <AlertCircle size={16} style={{ color: 'var(--danger)', flexShrink: 0, marginTop: 2 }} />
                        <span style={{ fontSize: 'var(--text-sm)', color: 'var(--danger)' }}>
                            Connection failed — please try again. If this persists, ensure you click 'Allow' on the Google permissions screen.
                        </span>
                    </div>
                )}

                {/* Local errors */}
                {localError && (
                    <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'flex-start', padding: 'var(--space-3)', background: 'rgba(239,68,68,0.08)', borderRadius: 'var(--radius-md)', border: '1px solid rgba(239,68,68,0.2)' }}>
                        <AlertCircle size={16} style={{ color: 'var(--danger)', flexShrink: 0, marginTop: 2 }} />
                        <span style={{ fontSize: 'var(--text-sm)', color: 'var(--danger)' }}>{localError}</span>
                    </div>
                )}

                {/* State: Not connected */}
                {!gaConnectedEmail && !gaPropertyId && gaConnectedParam !== '1' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)', flexWrap: 'wrap' }}>
                        <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', flex: 1 }}>
                            Connect your GA4 property to overlay externally-hosted site traffic data in this tab.
                        </p>
                        <GlassButton
                            variant="primary"
                            icon={<ExternalLink size={15} />}
                            onClick={handleConnect}
                            loading={connecting}
                            disabled={!projectId}
                        >
                            Connect Google Analytics
                        </GlassButton>
                    </div>
                )}

                {/* State: OAuth just completed — pick a property */}
                {gaConnectedParam === '1' && !gaPropertyId && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                        <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text)' }}>
                            Google account connected. Select the GA4 property for this project:
                        </p>
                        {loadingProps ? (
                            <div className="analytics-loading">
                                <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
                                Loading properties…
                            </div>
                        ) : properties.length === 0 ? (
                            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', margin: 0 }}>
                                No GA4 properties found in this account.
                            </p>
                        ) : (
                            <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                                <div style={{ flex: 1, minWidth: 200 }}>
                                    <GlassSelect
                                        label="GA4 Property"
                                        options={properties.map(p => ({ value: p.property_id, label: `${p.display_name} (${p.account_name})` }))}
                                        value={selectedProp}
                                        onChange={setSelectedProp}
                                        placeholder="Select a property…"
                                    />
                                </div>
                                <GlassButton
                                    variant="primary"
                                    onClick={handleSelectProperty}
                                    loading={savingProp}
                                    disabled={!selectedProp}
                                >
                                    Save
                                </GlassButton>
                            </div>
                        )}
                    </div>
                )}

                {/* State: Fully connected */}
                {isConnectedWithProperty && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 'var(--space-3)' }}>
                        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
                            <span style={{ color: 'var(--text)' }}>{gaConnectedEmail || 'Google account'}</span>
                            {gaPropertyName && (
                                <> &middot; <span style={{ color: 'var(--text)' }}>{gaPropertyName}</span></>
                            )}
                        </div>
                        <GlassButton
                            variant="secondary"
                            icon={<Unplug size={15} />}
                            onClick={handleDisconnect}
                            loading={disconnecting}
                        >
                            Disconnect
                        </GlassButton>
                    </div>
                )}
            </div>
        </GlassCard>
    );
};

// ---------------------------------------------------------------------------
// Tab: Traffic
// ---------------------------------------------------------------------------

const TrafficTab: React.FC<{ projectId?: string }> = ({ projectId }) => {
    const location = useLocation();
    const params = new URLSearchParams(location.search);

    const [days, setDays] = useState(30);
    const [source, setSource] = useState<'aime' | 'ga'>('aime');
    const [data, setData] = useState<TrafficResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [successBanner, setSuccessBanner] = useState(params.get('ga_connected') === '1');
    const [project, setProject] = useState<any>(null);
    // Data loading waits for the project lookup so a GA-connected project never flashes
    // (or wastes a request on) AIME data before switching to Google Analytics.
    const [projectLoaded, setProjectLoaded] = useState(false);
    const [fallbackNotice, setFallbackNotice] = useState<string | null>(null);

    // Load project to check GA connection state. Default source is Google Analytics when a
    // property is connected; applied once per visit (not remembered), so a manual switch to
    // AIME sticks for the rest of this visit but resets next time.
    useEffect(() => {
        if (!projectId) return;
        setProjectLoaded(false);
        getProject(projectId)
            .then(p => {
                setProject(p);
                setSource(p?.ga_property_id ? 'ga' : 'aime');
                setProjectLoaded(true);
            })
            .catch(() => { setProjectLoaded(true); });
    }, [projectId]);

    // Disconnecting (or an unconnected project) must never leave the GA source selected.
    useEffect(() => {
        if (projectLoaded && !project?.ga_property_id && source === 'ga') setSource('aime');
    }, [projectLoaded, project, source]);

    const loadData = useCallback(async () => {
        if (!projectId || !projectLoaded) return;
        setLoading(true);
        setError(null);
        try {
            const d = await getTraffic(projectId, days, source);
            setData(d);
        } catch (e: any) {
            const msg = e?.response?.data?.detail || 'Failed to load traffic data.';
            if (source === 'ga') {
                // Google Analytics failed (revoked token, API error): show AIME data with a
                // notice instead of an empty tab. Switching source triggers the AIME reload.
                setFallbackNotice(`Couldn't load Google Analytics data (${msg}). Showing AIME Analytics instead.`);
                setSource('aime');
            } else {
                setError(msg);
            }
        } finally {
            setLoading(false);
        }
    }, [projectId, projectLoaded, days, source]);

    useEffect(() => { loadData(); }, [loadData]);

    // Dismiss success banner after 5s
    useEffect(() => {
        if (!successBanner) return;
        const t = setTimeout(() => setSuccessBanner(false), 5000);
        return () => clearTimeout(t);
    }, [successBanner]);

    const gaConnected = !!project?.ga_property_id;

    const DAY_OPTIONS = [7, 14, 30, 90];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>

            {/* Success banner */}
            {successBanner && (
                <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center', padding: 'var(--space-3) var(--space-4)', background: 'rgba(16,185,129,0.10)', borderRadius: 'var(--radius-md)', border: '1px solid rgba(16,185,129,0.25)' }}>
                    <CheckCircle size={16} style={{ color: 'var(--success)', flexShrink: 0 }} />
                    <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text)' }}>Google Analytics connected — select a property below to start seeing GA data.</span>
                </div>
            )}

            {/* Controls row */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 'var(--space-3)' }}>
                {/* Source toggle — only show when GA is connected */}
                {gaConnected ? (
                    <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
                        <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>Source:</span>
                        <div style={{ display: 'flex', background: 'var(--panel-strong)', borderRadius: 'var(--radius-md)', border: '1px solid var(--stroke)', overflow: 'hidden' }}>
                            {(['aime', 'ga'] as const).map(s => (
                                <button
                                    key={s}
                                    onClick={() => { setFallbackNotice(null); setSource(s); }}
                                    style={{
                                        padding: 'var(--space-2) var(--space-3)',
                                        fontSize: 'var(--text-sm)',
                                        fontFamily: 'var(--font-family)',
                                        border: 'none',
                                        cursor: 'pointer',
                                        background: source === s ? 'var(--primary-500)' : 'transparent',
                                        color: source === s ? '#fff' : 'var(--text-secondary)',
                                        transition: 'background 0.15s',
                                    }}
                                >
                                    {s === 'aime' ? 'AIME Analytics' : 'Google Analytics'}
                                </button>
                            ))}
                        </div>
                    </div>
                ) : <div />}

                {/* Days selector */}
                <div style={{ display: 'flex', gap: 'var(--space-1)', alignItems: 'center' }}>
                    <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', marginRight: 'var(--space-1)' }}>Last:</span>
                    {DAY_OPTIONS.map(d => (
                        <button
                            key={d}
                            onClick={() => setDays(d)}
                            style={{
                                padding: 'var(--space-1) var(--space-3)',
                                fontSize: 'var(--text-sm)',
                                fontFamily: 'var(--font-family)',
                                borderRadius: 'var(--radius-md)',
                                border: '1px solid var(--stroke)',
                                cursor: 'pointer',
                                background: days === d ? 'var(--primary-500)' : 'var(--panel-strong)',
                                color: days === d ? '#fff' : 'var(--text-secondary)',
                                transition: 'background 0.15s',
                            }}
                        >
                            {d}d
                        </button>
                    ))}
                </div>
            </div>

            {fallbackNotice && source === 'aime' && (
                <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center', padding: 'var(--space-3) var(--space-4)', background: 'rgba(245,158,11,0.10)', borderRadius: 'var(--radius-md)', border: '1px solid rgba(245,158,11,0.25)' }}>
                    <AlertCircle size={16} style={{ color: 'var(--warning)', flexShrink: 0 }} />
                    <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text)' }}>{fallbackNotice}</span>
                </div>
            )}

            {/* Loading */}
            {loading && (
                <div className="analytics-loading">
                    <RefreshCw size={18} style={{ animation: 'spin 1s linear infinite' }} />
                    Loading traffic data…
                </div>
            )}

            {/* Error */}
            {error && !loading && (
                <div className="analytics-empty">
                    <AlertCircle size={18} /> {error}
                </div>
            )}

            {/* No project selected */}
            {!projectId && !loading && (
                <div className="analytics-empty">
                    <Globe size={18} /> Select a project to view traffic data.
                </div>
            )}

            {/* Data */}
            {data && !loading && (
                <>
                    {/* KPI tiles */}
                    <div className="analytics-kpi-row">
                        <KPICard
                            value={data.summary.page_views.toLocaleString()}
                            label="Page Views"
                            icon={<Eye size={20} />}
                        />
                        <KPICard
                            value={data.summary.unique_visitors.toLocaleString()}
                            label="Unique Visitors"
                            icon={<Users size={20} />}
                        />
                        <KPICard
                            value={formatSeconds(data.summary.avg_time_seconds)}
                            label="Avg. Time on Page"
                            icon={<Timer size={20} />}
                        />
                        <KPICard
                            value={`${data.summary.engaged_session_pct.toFixed(1)}%`}
                            label="Engaged Sessions"
                            icon={<MousePointerClick size={20} />}
                        />
                    </div>

                    {/* Line chart */}
                    {data.daily.length > 0 ? (
                        <GlassCard variant="default" padding="md">
                            <h3 className="analytics-section-title" style={{ marginBottom: 'var(--space-4)' }}>
                                Daily Traffic — Last {data.date_range.days} Days
                            </h3>
                            <MiniLineChart data={data.daily} />
                        </GlassCard>
                    ) : null}

                    {/* Top pages */}
                    {data.top_pages.length > 0 && (
                        <GlassCard variant="default" padding="md">
                            <h3 className="analytics-section-title">Top Pages</h3>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', marginTop: 'var(--space-3)' }}>
                                {data.top_pages.map((p, i) => (
                                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 'var(--space-2) var(--space-3)', background: 'var(--panel-strong)', borderRadius: 'var(--radius-md)', border: '1px solid var(--stroke)' }}>
                                        <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text)', fontFamily: 'var(--font-mono, monospace)' }}>
                                            {p.page_slug.length > 40 ? '…' + p.page_slug.slice(-37) : p.page_slug}
                                        </span>
                                        <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', flexShrink: 0, marginLeft: 'var(--space-4)' }}>
                                            {p.page_views.toLocaleString()} views
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </GlassCard>
                    )}

                    {/* Empty state */}
                    {data.summary.page_views === 0 && (
                        <div className="analytics-empty">
                            <Globe size={18} /> No traffic data yet — publish a page and wait for visitors.
                        </div>
                    )}
                </>
            )}

            {/* UTM attribution breakdown (B3) — fetches its own data, mounted
                outside the `data &&` wrapper so a traffic failure never hides
                it and a breakdown failure never blanks the KPIs. */}
            {projectId && <UtmBreakdownPanel projectId={projectId} days={days} />}

            {/* GA Connect section — gated at call-site by feature flag so the
                hook-using GAConnectCard isn't instantiated while disabled. */}
            {projectId && !GA_INTEGRATION_ENABLED && <GAComingSoonCard />}
            {projectId && GA_INTEGRATION_ENABLED && (
                <GAConnectCard
                    projectId={projectId}
                    gaConnectedEmail={project?.ga_connected_email}
                    gaPropertyId={project?.ga_property_id}
                    gaPropertyName={project?.ga_property_name}
                    onConnected={() => {
                        // Reload project state after connect/disconnect
                        getProject(projectId).then(setProject).catch(() => {});
                    }}
                />
            )}

            {/* Connect prompt if no project */}
            {!projectId && (
                <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', textAlign: 'center' }}>
                    Select a project to manage Google Analytics connection.
                </div>
            )}
        </div>
    );
};

// ---------------------------------------------------------------------------
// Main AnalyticsModule
// ---------------------------------------------------------------------------

const AnalyticsModule: React.FC = () => {
    const [activeTab, setActiveTab] = useState('overview');
    const ctx = useOutletContext<OutletCtx>();
    const projectId = ctx?.selectedProjectId;

    return (
        <div className="analytics-module">
            <ModuleHeader
                title="Analytics & Reporting"
                breadcrumb={['Analytics']}
            />

            <div className="analytics-tabs-row">
                <GlassTabs
                    tabs={TABS}
                    activeTab={activeTab}
                    onChange={setActiveTab}
                    size="sm"
                />
            </div>

            <div className="analytics-content">
                {activeTab === 'overview' && <OverviewTab projectId={projectId} />}
                {activeTab === 'campaign' && <CampaignPerformanceTab />}
                {activeTab === 'email' && <EmailMetricsTab />}
                {activeTab === 'content' && <ContentPerformanceTab projectId={projectId} />}
                {activeTab === 'revenue' && <RevenueAnalytics />}
                {activeTab === 'monthly' && <MonthlySummaryTab projectId={projectId} />}
                {activeTab === 'traffic' && <TrafficTab projectId={projectId} />}
            </div>
        </div>
    );
};

export default AnalyticsModule;
